import { Component, Host, h, ComponentInterface, Element, Prop, Watch, Event, EventEmitter } from '@stencil/core';
import { FlexDirectionOptions, FlexContentOptions, PredefinedColors } from '../../interface';

@Component({
    tag: 'rs-radio-group',
    styleUrl: 'radio-group.css',
    shadow: true
})
export class RadioGroup implements ComponentInterface {

    /**
     * The host element within the class instance.
     * This returns an instance of HTMLStencilElement which extends HTMLElement, so standard DOM methods/events can be used.
     */
    @Element() el: HTMLRsRadioGroupElement;

    /**
     * If this is true, then user can deselect radios
     */
    @Prop() allowDeselect = false;

    /**
     * The value of the selected radio
     */
        // eslint-disable-next-line @stencil/strict-mutable
    @Prop({ mutable: true }) value?: any | null;

    /**
     * The name of the control, which will be submitted with the form data.
     */
    @Prop() name: string;

    /**
     * (optional) 'primary' | 'secondary' | 'secondary-variant' | 'positive' | 'positive-variant' | 'white' | 'light' | 'grey-light' | 'grey' | 'black' | 'heading' | 'negative' | 'neutral' | 'highlight'
     * Colour of the inner circle, the outline will just be a standard grey colour.
     * You can pass custom colour in hex or rgb format (e.g. '#ffffff' or 'rgb(12,32,79)').
     */
    @Prop() color: PredefinedColors | string;

    /**
     * An event emitted when the value has changed.
     */
    @Event() rsChange: EventEmitter;

    /**
     * If true, we display all the elements inside the radio group in a single row.
     */
    @Prop() flex: boolean = false;

    /**
     * Possible values are: flex-start, flex-end, space-around, space-between, space-evenly
     * Defines how we want to align the elements inside the radio group
     */
    @Prop() justifyContent: FlexContentOptions;

    /**
     * Useful when using Flex display. Sets the flex-direction css style
     */
    @Prop() flexDirection: FlexDirectionOptions;

    /**
     * Changes the look of the radio component to a grey outline button
     */
    @Prop() button?: 'vertical' | 'horizontal' | 'default' = 'default';

    /**
     * Watch for any changes on the `value` property.
     */
    @Watch('value')
    valueChanged(value: any | undefined) {
        this.rsChange.emit({ value });
    }

    componentDidLoad() {
        const radioItems = this.el.querySelectorAll('rs-radio'); // Get all the radio items
        radioItems.forEach(radioItem => {
            // set inner circle color of the radio components inside this radio group
            if (this.color !== undefined) {
                radioItem.setAttribute('color', this.color);
            }

            // set the button look of the radio components inside this radio group
            if (this.button !== undefined) {
                radioItem.setAttribute('button', this.button);
            }

        });
    }

    render() {
        return (
            <Host
                role="radiogroup"
                class={{
                    'rs-radio-group--flex': this.flex
                }}
                style={{
                    'justify-content': this.justifyContent,
                    'flex-direction': this.flexDirection
                }}
            >
                <slot/>
            </Host>
        );
    }

}